// route-engine.js — pure operator-pricing engine (Phase 1, op-pricing rebuild).
//
// Computes an operator's BROKER amount (what the operator receives, NET of VAT)
// for a route under the "Automatic" per-aircraft model. NO platform markup and
// NO VAT are applied here — the broker→customer markup AND the single VAT step
// stay platform-side (src/lib/pricing.js brokerToCustomer). The output is the
// operator-facing net number (net rate in → net amount out) and a fully-itemised
// breakdown the shared <Breakdown> component renders verbatim in both the
// simulator and the per-aircraft route prices.
//
// Locked model (do not reinterpret):
//   total flight minutes = fm(base→pickup) + fm(pickup→…→dropoff) + fm(dropoff→base)
//     fm = calcFlightMin(haversine_km, speedKmh)  — includes the 6-min taxi+approach
//   rotor cost = (total block minutes / 60) × ratePerHour
//   your amount = rotor cost + Σ(landing fees touched) + fixed
//   if your amount < minimum → raise to minimum (shown in breakdown)
//
//   Base form = pure flight time. Block buffer / minimum / fixed default OFF.
//   v1 ferry assumption: the aircraft returns to base after the last drop-off
//   (positioning leg out + return leg home are always included). A round trip
//   adds the return revenue leg(s); demurrage (wait-at-destination) is v2.
//
// PURE: no Sanity, no I/O, no Date.now — fully unit-testable. Coordinates are
// {lat,lng}; the caller resolves them from the locations array.

import { haversineKm } from '../flightTime.js';

// Round to whole euro — operators deal in whole euros; rounding each money line
// and summing keeps the breakdown adding up exactly.
function eur(n) { return Math.round(n); }

/**
 * Per-leg flight time. Pure cruise by default; `overheadMin` (operator-defined
 * taxi/approach, default 0) is added per leg; the optional per-helipad-type
 * block buffer is ADDITIONAL ground time on top.
 * @returns {{from,to,km,flightMin,bufferMin,blockMin}|null} null if coords/speed missing
 */
export function legTime(from, to, speedKmh, bufferMin = 0, overheadMin = 0) {
  const km = haversineKm(from, to);
  if (km == null || !speedKmh || speedKmh <= 0) return null;
  // RAW (unrounded) cruise + operator-defined per-leg overhead (taxi/approach,
  // default 0). Rounding happens ONCE on the route total (priceRouteAuto /
  // costFlightHours) — round(Σ raw leg minutes), NOT Σ round(leg) — to match the
  // real operator calc. Per-leg display rounds for presentation only.
  const flightMin = (km / speedKmh) * 60 + (Number(overheadMin) || 0);
  const buf = Math.max(0, Math.round(bufferMin || 0));
  return { from, to, km, flightMin, bufferMin: buf, blockMin: flightMin + buf };
}

// Per-leg buffer from the per-helipad-type config: taxi at departure pad +
// approach at arrival pad. Only used when applyBlockBuffer is on.
function legBuffer(fromType, toType, buffer) {
  if (!buffer) return 0;
  const taxi = fromType === 'airport' ? (buffer.airportTaxiMin || 0) : (buffer.helipadTaxiMin || 0);
  const appr = toType === 'airport' ? (buffer.airportApproachMin || 0) : (buffer.helipadApproachMin || 0);
  return (taxi || 0) + (appr || 0);
}

/**
 * Build the full waypoint sequence the aircraft actually flies, base-anchored:
 *   base → revenue stops (in order) → base
 * Round trip = the one-way base-round flown TWICE — the heli ferries HOME between
 * dropping the customer off and picking them up again; it does NOT wait. So the
 * round-trip distance, flight time and leg count are EXACTLY 2× the one-way by
 * construction (matches the operator calc, e.g. Helistar LGAV↔ZTH 539km/2.0h
 * one-way → 1078km/4.0h round trip):
 *   one-way [A,B]            → base, A, B, base
 *   round trip [A,B]         → base, A, B, base, A, B, base   (two one-way rounds)
 *   one-way with stop [A,S,B]→ base, A, S, B, base
 * (Was base→A→B→A→base — a SINGLE base round that collapsed to ~1× one-way when
 * base==pickup, undercharging the round trip by ~half.)
 * @param {Object} base   {lat,lng,_id,name,type} or null
 * @param {Array}  stops  ordered revenue waypoints [{lat,lng,_id,name,type}] (≥2: pickup…dropoff)
 * @param {boolean} roundTrip
 * @returns {Array} ordered node list including base at both ends (base may be null)
 */
export function buildSequence(base, stops, roundTrip) {
  if (roundTrip && stops.length >= 1) return [base, ...stops, base, ...stops, base];
  return [base, ...stops, base];
}

/**
 * Compute the operator broker amount + breakdown for one aircraft on one route.
 *
 * @param {Object} args
 * @param {Object|null} args.base           home base {lat,lng,_id,name,type}
 * @param {Array}       args.stops          revenue waypoints [pickup, …mid, dropoff]
 * @param {boolean}     args.roundTrip
 * @param {number}      args.speedKmh
 * @param {number}      args.ratePerHour    operator broker €/h (net, excl. VAT)
 * @param {Function}    args.feeFor         (location) => { brokerFee:number|null, source:'override'|'none' }
 * @param {number}      [args.minimumPerBooking]
 * @param {number}      [args.fixedPerBooking]
 * @param {Object|null} [args.buffer]       { airportTaxiMin, airportApproachMin, helipadTaxiMin, helipadApproachMin }
 * @param {boolean}     [args.applyBlockBuffer=false]
 * @param {boolean}     [args.countBaseAsFee=false]
 * @returns {{ok:true, breakdown:Object} | {ok:false, reason:string}}
 *   reason ∈ 'no-rate' | 'no-base' | 'no-stops' | 'no-coords'
 */
export function priceRouteAuto(args) {
  const {
    base, stops, roundTrip = false, speedKmh, ratePerHour,
    feeFor = () => ({ brokerFee: null, source: 'none' }),
    minimumPerBooking, fixedPerBooking,
    buffer = null, applyBlockBuffer = false, countBaseAsFee = false,
    overheadMin = 0, floorBlockMin = false,
  } = args || {};

  if (!(ratePerHour > 0)) return { ok: false, reason: 'no-rate' };
  if (!base || base.lat == null || base.lng == null) return { ok: false, reason: 'no-base' };
  if (!Array.isArray(stops) || stops.length < 2) return { ok: false, reason: 'no-stops' };

  const seq = buildSequence(base, stops, roundTrip);

  // Build legs base→…→base. Any leg with missing coords/speed → not auto-priceable.
  const legs = [];
  for (let i = 0; i < seq.length - 1; i++) {
    const a = seq[i], b = seq[i + 1];
    const buf = applyBlockBuffer ? legBuffer(a?.type, b?.type, buffer) : 0;
    const lt = legTime(a, b, speedKmh, buf, overheadMin);
    if (!lt) return { ok: false, reason: 'no-coords' };
    // Label each leg by whether it touches the BASE node (same object inserted by
    // buildSequence, so reference equality is exact even when base==pickup coords):
    //   stop→base = return (ferry home) · base→stop = positioning (ferry out) ·
    //   stop→stop = revenue (the passenger segment).
    // For a round trip [base,…stops,base,…stops,base] this correctly tags the INNER
    // stop→base and base→stop legs between the two passenger rounds as ferry too —
    // the prior i===0 / i===last test mislabeled them 'revenue' → "Passenger leg".
    const role = b === base ? 'return' : (a === base ? 'positioning' : 'revenue');
    legs.push({ ...lt, role, fromName: a?.name || '', toName: b?.name || '' });
  }

  // RAW total block time (Σ raw leg minutes). Default: NO money-path rounding (the
  // raw path keeps round-trip == exactly 2× one-way; the customer "~539 km / ~2h"
  // display rounds separately, cosmetic). floorBlockMin (the Automatic money path):
  // FLOOR the WHOLE-trip block to whole minutes ONCE — floor(Σ legs), NOT per-leg and
  // NOT one-way-floored×2 — adopting the cost-model's costFlightHours semantics
  // (pricing.js:91). Consequence (owner-confirmed): RT = floor(2×oneWayRaw), which is
  // NOT 2×floor(oneWayRaw) — Automatic RT is then ≤1 min more than 2× one-way, exactly
  // like cost-model/HELISTAR. The floored value is returned in breakdown.totalBlockMin
  // so the display (plain hm()) shows the same whole minutes that are priced.
  const totalBlockMin = floorBlockMin
    ? Math.floor(legs.reduce((s, l) => s + l.blockMin, 0))
    : legs.reduce((s, l) => s + l.blockMin, 0);
  const rotorCostRaw = (totalBlockMin / 60) * ratePerHour;
  const rotorCost = eur(rotorCostRaw);

  // Landing fees: each DISTINCT touched location (excl. base unless countBaseAsFee).
  // Every touched pad appears as a line — incl. "no fee set — €0" — so a
  // forgotten fee is visible and never a silent €0.
  const baseId = base?._id;
  const seenFee = new Set();
  const feeLines = [];
  for (const node of seq) {
    if (!node || !node._id) continue;
    const isBase = node._id === baseId;
    if (isBase && !countBaseAsFee) continue;
    if (seenFee.has(node._id)) continue;
    seenFee.add(node._id);
    const f = feeFor(node) || { brokerFee: null, source: 'none' };
    const brokerFee = Number.isFinite(Number(f.brokerFee)) && Number(f.brokerFee) > 0 ? eur(Number(f.brokerFee)) : 0;
    feeLines.push({
      _id: node._id, name: node.name || node._id, type: node.type || '',
      brokerFee, source: brokerFee > 0 ? (f.source || 'override') : 'none', isBase,
    });
  }
  const feesTotal = feeLines.reduce((s, l) => s + l.brokerFee, 0);

  const fixed = Number.isFinite(Number(fixedPerBooking)) && Number(fixedPerBooking) > 0 ? eur(Number(fixedPerBooking)) : 0;

  // FEES-SEPARATE MODEL (confirmed 2026-06). The route price is TIME-BASED
  // only — rotor + fixed. Landing fees are NOT summed into it: they stay
  // additive per ACTUAL pad at booking (Option-Y — src/lib/pricing.js
  // getHelipadFee + bookings-create + App.jsx), so the customer is never
  // double-charged. feeLines/feesTotal are returned ONLY for transparent
  // display ("added per landing pad at booking"). The per-booking minimum
  // floors the charter itself (rotor + fixed), not the pad fees.
  const routeBeforeMin = rotorCost + fixed;
  const minimum = Number.isFinite(Number(minimumPerBooking)) && Number(minimumPerBooking) > 0 ? eur(Number(minimumPerBooking)) : 0;
  const minimumApplied = minimum > 0 && routeBeforeMin < minimum;
  const routeAmount = minimumApplied ? minimum : routeBeforeMin;
  const typicalTotalWithFees = routeAmount + feesTotal; // info only — fees billed per pad at booking

  return {
    ok: true,
    breakdown: {
      legs,
      totalBlockMin,
      blockFloored: !!floorBlockMin, // display: totalBlockMin floored to whole minutes
      ratePerHour,
      rotorCost,
      rotorCostRaw,
      fixed,
      routeBeforeMin,
      minimum,
      minimumApplied,
      routeAmount,            // operator BROKER route price, time-based, NET (excl. VAT) — NO markup, NO VAT, NO landing fees
      feeLines,               // touched pads — shown separately, added per pad at booking
      feesTotal,
      typicalTotalWithFees,   // routeAmount + feesTotal — display info only, NOT the stored price
      roundTrip,
    },
  };
}
